import { useState } from 'react';
import './Learn.css';

const disabilities = [
  {
    id: 'lissencephaly',
    emoji: '🧠',
    name: 'Lissencephaly',
    subtitle: "Izzi's condition — a rare and special brain difference",
    color: 'lavender',
    featured: true,
    whatIsIt: `Lissencephaly (say it like this: "lis-en-SEF-uh-lee") means "smooth brain" in Greek. Usually our brains have lots of folds and bumps — those wrinkles help brain cells connect and talk to each other. But kids with lissencephaly have a brain that is much smoother than usual. This happens before a baby is born, when the brain is still growing.`,
    howItAffects: `Every child with lissencephaly is different! Some things that might be harder include: learning new skills like walking or talking, controlling muscles, and some kids might have seizures (which are like little electrical storms in the brain). Many kids with lissencephaly use wheelchairs, and most communicate in special ways like sign language or AAC devices (special tablets that speak for them). Even though some things are harder, kids with lissencephaly still experience joy, love music, laugh, and have amazing personalities!`,
    howToFriend: [
      'Say hello and introduce yourself — they love meeting new people!',
      'Speak TO them, not around them — they understand more than you might think',
      'Be patient if they take a little longer to respond',
      'Ask their parent or caregiver the best ways to communicate',
      'Do activities together like listening to music, watching movies, or blowing bubbles',
      'Treat them like any other friend — because they ARE!',
    ],
    didYouKnow: 'Lissencephaly affects about 1 in 100,000 people. Izzi is one in a million! 💜',
  },
  {
    id: 'autism',
    emoji: '🌈',
    name: 'Autism',
    subtitle: 'A different — and amazing — way of seeing the world',
    color: 'mint',
    whatIsIt: `Autism (also called Autism Spectrum Disorder or ASD) is when someone's brain develops and works in a unique way. Autism is called a "spectrum" because it affects everyone differently — no two autistic people are exactly the same! Some autistic kids might be really good at remembering facts or patterns, while others might experience the world very intensely through their senses.`,
    howItAffects: `Autistic kids might find some social situations confusing or overwhelming. Loud noises, bright lights, or unexpected changes can be hard. Some autistic kids don't like being touched. Some kids might repeat certain movements (called "stimming") to help themselves feel calm — like rocking, flapping hands, or spinning. Many autistic kids have amazing special interests they know everything about! Some autistic kids don't use words to speak, but they can still communicate in other great ways.`,
    howToFriend: [
      'Don\'t force eye contact — it can be uncomfortable for some autistic kids',
      'Respect their space and ask before hugging or touching',
      'If they have a special interest, ask about it — they\'ll love talking about it!',
      'Be okay with quiet — silence is okay when hanging out',
      'Keep promises and plans — surprises can be stressful',
      'If something upsets them, stay calm and ask how you can help',
    ],
    didYouKnow: 'About 1 in 36 kids is autistic. That means you probably already know an autistic person! 🌈',
  },
  {
    id: 'down-syndrome',
    emoji: '🌟',
    name: 'Down Syndrome',
    subtitle: 'An extra chromosome makes an extra special person!',
    color: 'peach',
    whatIsIt: `Down Syndrome happens when someone is born with an extra copy of chromosome 21. Chromosomes are tiny things inside every cell of our body that carry instructions for how we grow. Most people have 46 chromosomes, but people with Down Syndrome have 47! That extra chromosome makes them wonderfully unique.`,
    howItAffects: `People with Down Syndrome might learn things at a slower pace and might need a bit more time to do some tasks. They might have some health differences too, like a heart condition that doctors can help with. But here's what's really cool — people with Down Syndrome are often known for being incredibly warm, friendly, funny, and loving! They go to school, make friends, have jobs, and live full, happy lives.`,
    howToFriend: [
      'Talk to them just like you\'d talk to any friend — they love conversations!',
      'Be patient and give them time to express themselves',
      'Include them in games and activities at school',
      'Don\'t use unkind words to describe Down Syndrome — it\'s hurtful',
      'Celebrate their achievements, big and small',
      'Ask to be their friend — most people with Down Syndrome are super friendly and would love that!',
    ],
    didYouKnow: 'Down Syndrome is the most common chromosomal condition. World Down Syndrome Day is on March 21st (3/21)! 🌟',
  },
  {
    id: 'cerebral-palsy',
    emoji: '🦋',
    name: 'Cerebral Palsy',
    subtitle: 'A difference in movement that makes each person unique',
    color: 'blue',
    whatIsIt: `Cerebral Palsy (CP) happens when a part of the brain that controls movement is injured, usually before or around birth. "Cerebral" means it involves the brain, and "palsy" means problems with movement. CP affects how a person moves their body — every person with CP experiences it differently.`,
    howItAffects: `Some kids with CP might use a wheelchair, walker, or crutches to get around. Others might walk with a different gait (the way you walk). Some might have arms or hands that are harder to control. Some kids with CP might also have differences in speech. But here's something important: CP doesn't change how smart or creative someone is! Many kids with CP are brilliant, funny, and full of amazing ideas.`,
    howToFriend: [
      'Don\'t stare at how someone moves — it\'s just their way of getting around',
      'If you see them struggling with something, ask "can I help?" before jumping in',
      'Keep pathways clear so wheelchairs or walkers can move easily',
      'Be patient if their speech sounds different — listen carefully',
      'Include them in every activity you can',
      'Ask about their interests — they\'ll have lots to share!',
    ],
    didYouKnow: 'Cerebral Palsy is the most common movement disability in children. Many athletes, artists, and actors have CP! 🦋',
  },
  {
    id: 'hearing-impairment',
    emoji: '🤟',
    name: 'Hearing Impairment',
    subtitle: 'A world of communication beyond sound',
    color: 'lavender',
    whatIsIt: `Hearing impairment means someone has difficulty hearing, or cannot hear at all. It can range from mild (a little hard to hear) to profound (very little or no hearing). Some people are born with it, others lose hearing over time. Kids with hearing impairment are not defined by what they can't hear — they have a rich, vibrant world all their own!`,
    howItAffects: `Kids with hearing impairments might wear hearing aids or cochlear implants (devices that help with hearing). Many use sign language to communicate — and sign language is a beautiful, full language! Some kids can read lips or use written notes. In classrooms, they might sit in special spots or use special equipment to hear teachers better.`,
    howToFriend: [
      'Learn a few basic signs in American Sign Language — it\'s so fun!',
      'Get their attention by gently tapping their shoulder before speaking',
      'Look at them when you speak — it helps with lip reading',
      'Speak clearly, but don\'t exaggerate your mouth movements',
      'Don\'t cover your mouth when talking to them',
      'If they didn\'t understand, rephrase rather than just repeating it more loudly',
    ],
    didYouKnow: 'Sign language isn\'t universal! Different countries have their own sign languages. American Sign Language (ASL) is used in the US and Canada. 🤟',
  },
  {
    id: 'visual-impairment',
    emoji: '👁️',
    name: 'Visual Impairment',
    subtitle: 'Experiencing the world with extraordinary senses',
    color: 'yellow',
    whatIsIt: `Visual impairment means someone has difficulty seeing, even with glasses or contacts. It ranges from low vision (some sight, but limited) to blindness (very little or no sight). Being visually impaired doesn't change who someone is or what they can accomplish — people with visual impairments do amazing things every day!`,
    howItAffects: `Kids with visual impairments might use a white cane to help navigate, have a guide dog, use braille (a reading system using raised dots), or use apps and technology to help them. Their other senses — like hearing and touch — are often extra sharp! In school, they might have large-print materials or a screen reader.`,
    howToFriend: [
      'Always identify yourself when you approach them — say "Hi, it\'s [your name]!"',
      'Ask if they\'d like help before guiding them anywhere',
      'Don\'t move their belongings without telling them',
      'Describe things around you so they can be part of the conversation',
      'Never pet or distract a guide dog while it\'s working',
      'Be their friend, not their helper — they have plenty of skills!',
    ],
    didYouKnow: 'Helen Keller, who was both blind and deaf, graduated from college, wrote books, and inspired millions of people worldwide! 👁️',
  },
  {
    id: 'adhd',
    emoji: '⚡',
    name: 'ADHD',
    subtitle: 'A brain wired for energy, creativity, and big ideas',
    color: 'mint',
    whatIsIt: `ADHD stands for Attention-Deficit/Hyperactivity Disorder. Kids with ADHD have brains that work in a high-energy, exciting way! They might find it harder to focus on things that feel boring, stay still for long periods, or wait their turn. But they often have incredible energy, creativity, and when something captures their interest, they can focus on it with super-powered attention called "hyperfocus"!`,
    howItAffects: `Kids with ADHD might seem restless, fidgety, or easily distracted. They might interrupt conversations or act without thinking sometimes — not because they're trying to be rude, but because their brain is zooming ahead! Some kids take medicine or do activities to help manage their energy. ADHD doesn't mean someone isn't smart — many brilliant inventors, athletes, and artists have ADHD!`,
    howToFriend: [
      'Be patient if they seem distracted — their brain is just very busy!',
      'Don\'t take it personally if they interrupt — help them remember to take turns',
      'Suggest active games and activities that let them move',
      'If they seem overwhelmed, suggest taking a short break outside',
      'Celebrate when they do focus on something — it takes real effort',
      'Remind them of things kindly — they\'re not being forgetful on purpose',
    ],
    didYouKnow: 'Famous people thought to have ADHD include Simone Biles, Justin Timberlake, and even Albert Einstein! ⚡',
  },
];

export default function Learn() {
  const [activeId, setActiveId] = useState('lissencephaly');
  const active = disabilities.find(d => d.id === activeId);

  return (
    <div className="learn fade-in">
      <section className="learn-hero">
        <div className="container">
          <h1 className="section-title">📚 Learn About Different Abilities</h1>
          <p className="section-subtitle">
            Every brain and body is unique! Let's learn about different conditions
            so we can be the best friends possible. 🌟
          </p>
        </div>
      </section>

      <div className="container learn-layout">
        {/* Sidebar */}
        <aside className="learn-sidebar">
          <div className="sidebar-title">Choose a topic:</div>
          {disabilities.map(d => (
            <button
              key={d.id}
              className={`sidebar-btn sidebar-${d.color} ${activeId === d.id ? 'active' : ''}`}
              onClick={() => setActiveId(d.id)}
            >
              <span className="sidebar-emoji">{d.emoji}</span>
              <span className="sidebar-name">{d.name}</span>
              {d.featured && <span className="sidebar-tag">Izzi's ❤️</span>}
            </button>
          ))}
        </aside>

        {/* Content */}
        <div className="learn-content" key={activeId}>
          <div className={`learn-header learn-header-${active.color} ${active.featured ? 'featured-header' : ''}`}>
            <span className="learn-emoji">{active.emoji}</span>
            <div>
              <h2>{active.name}</h2>
              <p className="learn-subtitle">{active.subtitle}</p>
              {active.featured && (
                <span className="featured-badge">💜 Izzi's condition — learn all about it!</span>
              )}
            </div>
          </div>

          <div className="learn-sections">
            <div className={`learn-section card learn-section-${active.color}`}>
              <h3>🔍 What is {active.name}?</h3>
              <p>{active.whatIsIt}</p>
            </div>

            <div className="learn-section card learn-section-neutral">
              <h3>💡 How does it affect kids?</h3>
              <p>{active.howItAffects}</p>
            </div>

            <div className="learn-section card learn-section-friend">
              <h3>🤝 How to be a great friend</h3>
              <ul className="friend-list">
                {active.howToFriend.map((tip, i) => (
                  <li key={i}>
                    <span className="friend-check">✅</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="did-you-know">
              <span className="dyk-emoji">💬</span>
              <div>
                <strong>Did You Know?</strong>
                <p>{active.didYouKnow}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
